import styled from "styled-components";
import type { SettlementPeriod } from "../../../../api/work-policy-application-api.types";
import { workTypeDesignTokens as tokens } from "../../../work-type-shared/work-type-design-tokens";
import { formatKoreanCalendarDate } from "./calendar-date";

export interface SelectiveSnapshot {
  selectiveCoreEnabled: boolean;
  selectiveCoreStart: string | null;
  selectiveCoreEnd: string | null;
  breakCalculationEnabled?: boolean;
  breakWorkMinutes?: number;
  breakMinutes?: number;
}

interface SelectiveApplicationFieldsProps {
  snapshot: SelectiveSnapshot;
  status: "loading" | "ready" | "error";
  periods: SettlementPeriod[];
  onRetry: () => void;
}

const Section = styled.section`
  display: grid;
  min-width: 0;
  gap: ${tokens.spacing.md};
`;

const OperatingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: ${tokens.spacing.sm};

  > div {
    display: grid;
    min-width: 0;
    gap: 6px;
    align-content: start;
    padding: 12px;
    background: ${tokens.color.surfaceMuted};
    border: 1px solid ${tokens.color.border};
    border-radius: ${tokens.radius.control};
  }

  small {
    color: ${tokens.color.textMuted};
  }

  strong {
    overflow-wrap: anywhere;
    font-size: 15px;
    line-height: 1.45;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ConfigurationNote = styled.p`
  margin: 0;
  padding: 10px 12px;
  color: ${tokens.color.textMuted};
  background: ${tokens.tone.approval.background};
  border-left: 3px solid ${tokens.tone.approval.border};
  line-height: 1.55;
`;

const SettlementHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacing.sm};
  align-items: baseline;
  justify-content: space-between;

  h4,
  p {
    margin: 0;
  }

  p {
    color: ${tokens.color.textMuted};
  }
`;

const SettlementCount = styled.span`
  display: inline-flex;
  min-height: 24px;
  align-items: center;
  padding: 2px 9px;
  color: ${tokens.tone.approval.text};
  background: ${tokens.tone.approval.background};
  border: 1px solid ${tokens.tone.approval.border};
  border-radius: ${tokens.radius.pill};
  font-size: 12px;
  font-weight: 700;
`;

const StateMessage = styled.div<{ $tone?: "neutral" | "danger" }>`
  padding: 12px;
  color: ${props => tokens.tone[props.$tone ?? "neutral"].text};
  background: ${props => tokens.tone[props.$tone ?? "neutral"].background};
  border: 1px solid ${props => tokens.tone[props.$tone ?? "neutral"].border};
  border-radius: ${tokens.radius.control};

  button {
    min-height: 40px;
    margin-left: 8px;
  }
`;

const SettlementHint = styled.p`
  margin: -8px 0 0;
  color: ${tokens.color.textMuted};
  font-size: 12px;
  line-height: 1.45;
`;

const SettlementScroll = styled.div`
  max-height: 248px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 4px 12px 4px 4px;
  border-block: 1px solid ${tokens.color.border};

  &:focus-visible {
    outline: ${tokens.focus.outline};
    outline-offset: ${tokens.focus.offset};
  }

  ol {
    display: grid;
    gap: 8px;
    margin: 0;
    padding: 8px 0 8px 32px;
  }

  li {
    padding: 8px 10px;
    background: ${tokens.color.surfaceMuted};
    border-radius: ${tokens.radius.control};
    line-height: 1.45;
  }
`;

function formatHours(minutes: number) {
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}시간`;
}

export function SelectiveApplicationFields({
  snapshot,
  status,
  periods,
  onRetry
}: SelectiveApplicationFieldsProps) {
  const coreTime =
    snapshot.selectiveCoreEnabled &&
    snapshot.selectiveCoreStart &&
    snapshot.selectiveCoreEnd
      ? `${snapshot.selectiveCoreStart.slice(0, 5)} ~ ${snapshot.selectiveCoreEnd.slice(0, 5)}`
      : "의무근로시간대 없음";
  const automaticBreak = snapshot.breakCalculationEnabled
    ? `${formatHours(snapshot.breakWorkMinutes ?? 0)}마다 ${snapshot.breakMinutes ?? 0}분 자동 휴게`
    : "자동 휴게 미사용";

  return (
    <Section aria-labelledby="selective-detail-title">
      <h4 id="selective-detail-title">근무시간 운영 기준</h4>
      <OperatingGrid>
        <div>
          <small>운영 방식</small>
          <strong>자율 선택</strong>
        </div>
        <div>
          <small>의무근로시간대</small>
          <strong>{coreTime}</strong>
        </div>
        <div>
          <small>정산 기준</small>
          <strong>실제 체류시간 - 자동 휴게</strong>
          {snapshot.breakCalculationEnabled !== undefined && <small>{automaticBreak}</small>}
        </div>
      </OperatingGrid>
      <ConfigurationNote>
        이 기준은 관리자 환경설정을 따르며 신청 화면에서 변경할 수 없습니다.
      </ConfigurationNote>
      <SettlementHeader>
        <div>
          <h4>월별 정산구간</h4>
          <p>서버에서 계산된 순서와 월말 보정 결과를 그대로 표시합니다.</p>
        </div>
        <SettlementCount>{periods.length}개 구간</SettlementCount>
      </SettlementHeader>
      {status === "loading" && (
        <StateMessage role="status">정산 구간을 계산하는 중입니다. 기존 결과는 아래에 유지됩니다.</StateMessage>
      )}
      {status === "error" && (
        <StateMessage role="alert" $tone="danger">
          정산 구간을 불러오지 못했습니다.
          <button type="button" onClick={onRetry} aria-label="월별 정산구간 다시 불러오기">
            다시 시도
          </button>
        </StateMessage>
      )}
      {status === "ready" && periods.length === 0 && (
        <StateMessage>계산이 완료되었으며 정산구간이 없습니다.</StateMessage>
      )}
      {periods.length > 5 && (
        <SettlementHint>목록 안에서 스크롤해 전체 구간을 확인할 수 있습니다.</SettlementHint>
      )}
      {periods.length > 0 && (
        <SettlementScroll
          role="region"
          aria-label={`월별 정산구간 ${periods.length}개`}
          tabIndex={0}
          data-overflow-owner="settlement-periods"
        >
          <ol>
          {periods.map(period => (
            <li key={period.settlementNo}>
                {formatKoreanCalendarDate(period.settlementStartDate)} ~{" "}
                {formatKoreanCalendarDate(period.settlementEndDate)}
            </li>
          ))}
          </ol>
        </SettlementScroll>
      )}
    </Section>
  );
}
